/* =========================================================
   VERSUS PRODUCT PERSONALISATION — IMPACT THEME
   Rev 5 — fixes MutationObserver feedback loop

   The personalisation UI renders in a Custom Liquid block,
   which on Impact is a SIBLING of the buy-buttons block and
   therefore outside the product form. Inputs outside a form
   are never serialized, so no line item properties reach
   the cart.

   Fix: the visible controls carry NO name attribute. On any
   change we write hidden mirror inputs into every product
   form for this product. Those mirrors are what Shopify
   actually receives.

   Rev 5 note: syncMirrors is now idempotent — it updates
   existing mirror inputs in place instead of removing and
   recreating them. Combined with the observer filter below,
   this stops sync from re-triggering itself.
   ========================================================= */

(function () {

  const PROPERTY_MAP = {
    text:      'Personalisation Text',
    effect:    'Font Effect',
    placement: 'Placement'
  };

  const FLAG_KEY = '_Requires Personalisation';


  /* Forms inside these containers belong to OTHER products
     and must never receive our mirrors. */

  const FOREIGN_FORM_CONTAINERS =
    '.horizontal-product-card,' +
    'quick-buy-modal,' +
    '.quick-buy-modal,' +
    '[id^="shopify-section-related-products"],' +
    '[id^="shopify-section-complementary"]';


  function init() {

    const root = document.querySelector('[data-vs-customization]');
    if (!root) return;

    const textInput = root.querySelector('#VS_CustomText');
    const preview   = root.querySelector('[data-vs-preview]');
    const counter   = root.querySelector('.vs-current-count');

    if (!textInput || !preview) return;


    /* -----------------------------------------------------
       TARGET FORMS
       ----------------------------------------------------- */

    function isOwnForm(form) {
      if (!form) return false;
      if (!form.action || form.action.indexOf('/cart/add') === -1) return false;
      if (form.closest(FOREIGN_FORM_CONTAINERS)) return false;
      return true;
    }

    function targetForms() {
      return Array.prototype.filter.call(
        document.querySelectorAll('form[action*="/cart/add"]'),
        isOwnForm
      );
    }


    /* -----------------------------------------------------
       CURRENT VALUES
       ----------------------------------------------------- */

    function isPersonalised() {
      return textInput.value.trim().length > 0;
    }

    function currentValues() {

      if (!isPersonalised()) return {};

      const effect = root.querySelector('[data-vs-prop="effect"]:checked');
      const place  = root.querySelector('[data-vs-prop="placement"]:checked');

      const values = {};
      values[PROPERTY_MAP.text] = textInput.value.trim();

      if (effect) values[PROPERTY_MAP.effect]    = effect.value;
      if (place)  values[PROPERTY_MAP.placement] = place.value;

      values[FLAG_KEY] = 'Yes';

      return values;
    }


    /* -----------------------------------------------------
       SYNC MIRRORS — IDEMPOTENT

       Reuses existing inputs where possible. If nothing has
       actually changed, this performs zero DOM mutations,
       so it cannot wake the observer.
       ----------------------------------------------------- */

    function syncMirrors() {

      const values = currentValues();
      const keys   = Object.keys(values);

      targetForms().forEach(function (form) {

        /* Remove mirrors that are no longer wanted */

        form.querySelectorAll('[data-vs-mirror]').forEach(function (el) {
          if (keys.indexOf(el.getAttribute('data-vs-mirror')) === -1) {
            el.remove();
          }
        });

        /* Create or update the rest */

        keys.forEach(function (key) {

          let input = form.querySelector(
            '[data-vs-mirror="' + CSS.escape(key) + '"]'
          );

          if (!input) {
            input = document.createElement('input');
            input.type = 'hidden';
            input.name = 'properties[' + key + ']';
            input.setAttribute('data-vs-mirror', key);
            form.appendChild(input);
          }

          if (input.value !== values[key]) {
            input.value = values[key];
          }

        });

      });

    }


    /* -----------------------------------------------------
       PREVIEW
       ----------------------------------------------------- */

    function updatePreview() {

      preview.textContent = textInput.value.trim() || 'Your Text';
      preview.style.fontFamily = "'Libre Baskerville', serif";

      preview.style.background           = 'none';
      preview.style.webkitBackgroundClip = 'initial';
      preview.style.backgroundClip       = 'initial';
      preview.style.webkitTextFillColor  = 'initial';
      preview.style.color                = '#ffffff';
      preview.style.textShadow           = 'none';

      const selected = root.querySelector('[data-vs-prop="effect"]:checked');
      if (!selected) return;

      if (selected.value === 'Gold') {
        preview.style.background =
          'linear-gradient(135deg,#9c6b18 0%,#f7e29a 25%,#c7952d 50%,#fff1a8 75%,#9c6b18 100%)';
        preview.style.webkitBackgroundClip = 'text';
        preview.style.backgroundClip       = 'text';
        preview.style.webkitTextFillColor  = 'transparent';
      }

      if (selected.value === 'Silver') {
        preview.style.background =
          'linear-gradient(135deg,#777 0%,#f5f5f5 25%,#aaa 50%,#fff 75%,#777 100%)';
        preview.style.webkitBackgroundClip = 'text';
        preview.style.backgroundClip       = 'text';
        preview.style.webkitTextFillColor  = 'transparent';
      }

      if (selected.value === 'Emboss') {
        preview.style.color = '#c8b7a4';
        preview.style.textShadow =
          '1px 1px 1px rgba(255,255,255,0.45),-1px -1px 1px rgba(0,0,0,0.75)';
      }

    }


    /* -----------------------------------------------------
       EVENTS
       ----------------------------------------------------- */

    root.addEventListener('input', function () {
      if (counter) counter.textContent = textInput.value.length;
      updatePreview();
      syncMirrors();
    });

    root.addEventListener('change', function () {
      updatePreview();
      syncMirrors();
    });


    /* -----------------------------------------------------
       VARIANT CHANGE

       Impact re-renders the buy-buttons block when the
       colour changes, destroying our mirrors. We watch for
       that, but the observer MUST ignore mirror nodes or it
       will trigger the sync that created them — the loop
       that froze the page in rev 4.
       ----------------------------------------------------- */

    document.addEventListener('variant:change',  syncMirrors);
    document.addEventListener('variant:changed', syncMirrors);

    let pending = null;

    function isOurNode(node) {
      return node.nodeType === 1 && node.hasAttribute('data-vs-mirror');
    }

    function isRelevant(records) {

      for (let i = 0; i < records.length; i++) {

        const r = records[i];
        const nodes = [].concat(
          [].slice.call(r.addedNodes),
          [].slice.call(r.removedNodes)
        );

        for (let j = 0; j < nodes.length; j++) {
          if (!isOurNode(nodes[j])) return true;
        }

      }

      return false;
    }

    const observer = new MutationObserver(function (records) {

      if (!isRelevant(records)) return;

      clearTimeout(pending);
      pending = setTimeout(syncMirrors, 150);

    });


    /* Scope to the product section only, not document.body */

    const scope =
      root.closest('[id^="shopify-section"]') ||
      document.querySelector('main') ||
      document.body;

    observer.observe(scope, { childList: true, subtree: true });


    /* -----------------------------------------------------
       LAST-MOMENT SYNC BEFORE SUBMIT
       ----------------------------------------------------- */

    document.addEventListener('click', function (event) {

      const trigger = event.target.closest(
        'button[type="submit"],[name="add"],button[is="product-form-button"]'
      );
      if (!trigger) return;

      const form = trigger.form ||
                   trigger.closest('form[action*="/cart/add"]');

      if (!isOwnForm(form)) return;

      syncMirrors();

    }, true);


    document.addEventListener('submit', function (event) {
      if (!isOwnForm(event.target)) return;
      syncMirrors();
    }, true);


    if (counter) counter.textContent = textInput.value.length;
    updatePreview();
    syncMirrors();

  }


  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();